import * as Tone from 'tone';
import { clamp } from '../config/music';
import type { AccentLevel } from '../config/meters';

/**
 * Its own percussive voice wired straight to the destination, so the click
 * stays audible regardless of the instrument's effects and volume.
 */
let synth: Tone.MembraneSynth | null = null;
let loop: Tone.Loop | null = null;

export type BeatListener = (level: AccentLevel) => void;
let onBeat: BeatListener | null = null;

/** Accent pattern, one entry per click; the loop cycles it each bar. */
let accents: readonly AccentLevel[] = [2, 0, 1, 0];

const transport = (): ReturnType<typeof Tone.getTransport> => Tone.getTransport();

export const isRunning = (): boolean => loop !== null;

export function setBpm(bpm: number): void {
  transport().bpm.value = clamp(bpm, 20, 400);
}

/** Update the accent pattern; takes effect on the next click, even while running. */
export function setAccents(next: readonly AccentLevel[]): void {
  accents = next.length > 0 ? next : [2];
}

/** Pitch and velocity for each stress level, so the accent is clearly audible. */
const VOICE: Record<AccentLevel, { note: string; velocity: number }> = {
  2: { note: 'C3', velocity: 1 },
  1: { note: 'G2', velocity: 0.7 },
  0: { note: 'C2', velocity: 0.5 },
};

export function start(bpm: number, pattern: readonly AccentLevel[], listener: BeatListener): void {
  setAccents(pattern);
  if (loop) return;
  synth ??= new Tone.MembraneSynth({
    pitchDecay: 0.008,
    octaves: 2,
    envelope: { attack: 0.001, decay: 0.12, sustain: 0 },
  }).toDestination();

  onBeat = listener;
  setBpm(bpm);
  let beat = 0;
  loop = new Tone.Loop((time) => {
    const level = accents[beat % accents.length] ?? 0;
    const voice = VOICE[level];
    synth?.triggerAttackRelease(voice.note, '32n', time, voice.velocity);
    Tone.getDraw().schedule(() => onBeat?.(level), time);
    beat++;
  }, '4n').start(0);

  const t = transport();
  t.position = 0;
  t.start();
}

export function stop(): void {
  if (!loop) return;
  loop.stop();
  loop.dispose();
  loop = null;
  onBeat = null;
  const t = transport();
  t.stop();
  t.position = 0;
}

export function toggle(bpm: number, pattern: readonly AccentLevel[], listener: BeatListener): boolean {
  if (loop) {
    stop();
    return false;
  }
  start(bpm, pattern, listener);
  return true;
}
